import React, { useContext, useState, createContext, useCallback, useMemo, useEffect } from "react";
import useSWRMutation from 'swr/mutation';
import * as api from '../api';

const JWT_TOKEN_KEY = 'jwtToken';
const USER_ID_KEY = 'userId';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({children}) => {

    const [token, setToken] = useState(localStorage.getItem(JWT_TOKEN_KEY));
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);
    const [isAuthed, setIsAuthed] = useState(false);

    useEffect(() => {
      const token = localStorage.getItem(JWT_TOKEN_KEY);
      setToken(token);
      api.setAuthToken(token);
      setIsAuthed(Boolean(token));

      console.log("Auth context initialized with token:", token ? "Present" : "None");
    
      const storedUserId = localStorage.getItem(USER_ID_KEY);
    
      const fetchUser = async () => {
        try {
          if (token && storedUserId) {
            const userData = await api.get(`/users/${storedUserId}`);
            setUser(userData);
          }
        } catch (err) {
          console.error("Kon user niet ophalen bij refresh:", err);
          setUser(null);
          setToken(null);
          localStorage.removeItem(JWT_TOKEN_KEY);
          localStorage.removeItem(USER_ID_KEY);
        } finally {
          setReady(true); 
        }
      };
    
      fetchUser();
    }, [token]);     

    const {
        isMutating: loading,
        error: loginError,
        trigger: doLogin,
    } = useSWRMutation('users/login', api.post);

    const {
        isMutating: registerLoading,
        error: registerError,
        trigger: doRegister,
      } = useSWRMutation('users/register', api.post);

    const setSession = useCallback(
        (token, user) => {
          setToken(token);
          setUser(user);
    
          localStorage.setItem(JWT_TOKEN_KEY, token);
          localStorage.setItem(USER_ID_KEY, user.userId);
          api.setAuthToken(token);
          console.log("Session set with token:", token ? "Present" : "None");
        },
        [],
      );

    const login = useCallback(
        async (email, password) => {
            try{
                const {token, user} = await doLogin({email, password});

                setSession(token, user);

                return true;
            } catch(error){
                console.error(error);
                throw error;
            }
        },
        [doLogin, setSession],
    );

    const register = useCallback(
      async (data) => {
        try {
          const { token, user } = await doRegister(data);
          setSession(token, user);
          return { success: true };
        } catch (error) {
          console.error(error);
          return {
            success: false,
            message:
              error?.response?.data?.message ||
              error?.message ||
              "Registratie mislukt",
          };
        }
      },
      [doRegister, setSession]
    );
    

    const logout= useCallback(
        () => {
            setToken(null);
            setUser(null);

            localStorage.removeItem(JWT_TOKEN_KEY);
            localStorage.removeItem(USER_ID_KEY);
        },
        [],
    );

    const authErrorMessage = useMemo(() => {
      if (loginError?.response?.data?.message) return loginError.response.data.message;
      if (loginError?.message) return loginError.message;
      return null;
    }, [loginError]);

    const value = useMemo(() => ({
        token,
        user,
        error: authErrorMessage,
        ready,
        isAuthed,
        loading,
        login,
        logout,
        register,
    }),
    [token, user, authErrorMessage, registerError, loading, registerLoading, ready, isAuthed, login, logout, register],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};